
// The main game scene for BlockStack
import { GAME_CONFIG } from './gameConfig';
import { initializeBoard, createBlock } from './gameState';
import { 
  drawGrid, 
  createGameBackground, 
  drawBlock, 
  drawBoard, 
  createUI,
} from './renderer';
import { setupInputHandlers, moveLeft, moveRight, moveDown, rotate, dropToBottom, togglePause } from './inputHandler';
import { updateScore, levelUp, startDropTimer } from './scoreManager';
import { lockCurrentBlock, endGame, restartGame } from './gameLifecycle';

// Define the BlockStackGameScene class only if Phaser is available
export let BlockStackGameScene = null;

// Create a function to initialize the game scene
export function initBlockStackGameScene() {
  // Only initialize if Phaser is not already defined
  if (BlockStackGameScene !== null) return BlockStackGameScene;
  
  // Check if Phaser is available
  if (typeof window.Phaser === 'undefined' || !window.Phaser.Scene) {
    console.error('Phaser not fully loaded when initializing game scene');
    return null;
  }
  
  // Now create the scene class when we're sure Phaser is loaded
  try {
    BlockStackGameScene = class BlockStackGameSceneClass extends window.Phaser.Scene {
      constructor() {
        super('BlockStackGame');
        
        // Game state
        this.board = [];
        this.currentBlock = null;
        this.nextBlock = null;
        this.dropTimer = null;
        this.lastMoveTime = 0;
        this.moveDelay = 100; // Milliseconds between moves
        this.score = 0;
        this.level = 1;
        this.linesCleared = 0;
        this.dropSpeed = GAME_CONFIG.initialSpeed;
        this.isGameActive = false;
        this.isPaused = false;
        
        // UI elements
        this.ui = null;
        this.gameOverDisplay = null;
        this.pauseDisplay = null;
        this.boardGraphics = [];
        
        // Store reference to game config for easy access
        this.gameConfig = GAME_CONFIG;
        
        // Bind methods to this instance
        this.moveLeft = () => moveLeft(this);
        this.moveRight = () => moveRight(this);
        this.moveDown = () => moveDown(this);
        this.rotate = () => rotate(this);
        this.dropToBottom = () => dropToBottom(this);
        this.togglePause = () => togglePause(this);
        this.updateScore = (points) => updateScore(this, points);
        this.levelUp = (newLevel) => levelUp(this, newLevel);
        this.lockCurrentBlock = () => lockCurrentBlock(this);
        this.endGame = () => endGame(this);
        this.restartGame = () => restartGame(this);
        this.drawBlock = drawBlock;
        this.createPauseDisplay = createUI;
      }

      preload() {
        console.log('Preloading BlockStack game assets...');
        // We're not loading any assets yet, but could add them here
      }

      create() {
        try {
          console.log('Creating BlockStack game scene...');
          
          // Create game background
          this.background = createGameBackground(this);
          
          // Draw the grid
          this.grid = drawGrid(this);
          
          // Initialize the board
          this.board = initializeBoard();
          
          // Create UI elements
          this.ui = createUI(this);
          
          // Create first block
          this.currentBlock = createBlock(this);
          drawBlock(this, this.currentBlock);
          
          // Set up input handlers
          setupInputHandlers(this);
          
          // Start the game loop
          this.isGameActive = true;
          startDropTimer(this);
          
          console.log('BlockStack game scene created successfully');
        } catch (error) {
          console.error('Error in BlockStack game scene creation:', error);
          // Try to recover gracefully
          this.isGameActive = false;
        }
      }

      update() {
        // This method is called every frame, we can add game logic here if needed
      }
      
      // Add a proper shutdown method to clean up resources when leaving the scene
      shutdown() {
        console.log('BlockStack game scene shutting down...');
        try {
          // Clear any active timers
          if (this.dropTimer) {
            this.dropTimer.destroy();
            this.dropTimer = null;
          }
          
          // Clear input handlers
          this.input.keyboard?.off('keydown');
          
          // Clear graphics objects
          if (this.boardGraphics) {
            this.boardGraphics.forEach(graphic => {
              if (graphic && !graphic.destroyed) {
                graphic.destroy();
              }
            });
            this.boardGraphics = [];
          }
          
          // Clear other graphics
          if (this.grid && !this.grid.destroyed) this.grid.destroy();
          if (this.background && !this.background.destroyed) this.background.destroy();
          
          // Clear UI elements
          if (this.ui) {
            Object.values(this.ui).forEach(element => {
              if (element && !element.destroyed) {
                element.destroy();
              }
            });
          }
          
          // Clear game over display
          if (this.gameOverDisplay) {
            Object.values(this.gameOverDisplay).forEach(element => {
              if (element && !element.destroyed) {
                element.destroy();
              }
            });
          }
          
          // Clear current block graphics
          if (this.currentBlock && this.currentBlock.blocks) {
            this.currentBlock.blocks.forEach(blockGraphic => {
              if (blockGraphic && !blockGraphic.destroyed) {
                blockGraphic.destroy();
              }
            });
          }
          
          console.log('BlockStack game scene shutdown complete');
        } catch (error) {
          console.error('Error during BlockStack game scene shutdown:', error);
        }
      }
      
      // Override the destroy method to make sure we clean everything
      destroy() {
        this.shutdown();
        super.destroy();
        console.log('BlockStack game scene destroyed');
      }
    };
    
    return BlockStackGameScene;
  } catch (error) {
    console.error('Error creating BlockStackGameScene class:', error);
    return null;
  }
}

// Initialize the game scene when this module is loaded
try {
  initBlockStackGameScene();
} catch (error) {
  console.error('Failed to initialize BlockStackGameScene on module load:', error);
}
